<template>
  <KkBox tag="div" border>
    <template #content>
      <h3 class="name">{{ $t('pages.CompaniesStorePost.postSettings') }}</h3>
      <template v-if="currentPostTimingOption.key === 'reserve'">
        <KkInfo v-if="needApproval" alert>
          {{ $t('pages.CompaniesStorePost.cautionReservationDescription') }}
        </KkInfo>

        <!-- 投稿日 -->
        <section class="section">
          <h4 class="label">{{ $t('pages.CompaniesStorePost.reservationDate') }}</h4>
          <DatePicker
            :initial-dates="reservationDate"
            :is-single="true"
            :disabled-dates="disabledDates"
            :is-display-day-of-week="true"
            @input="changeReservationDate"
          />
        </section>

        <!-- 投稿時間 -->
        <section class="section">
          <h4 class="label">{{ $t('pages.CompaniesStorePost.reservationTime') }}</h4>
          <div class="reservationTime">
            <div class="reservationTimeHours">
              <Autocomplete
                id="reservationHours"
                :data-list="hoursList"
                :value="reservationHours"
                :searchable="false"
                placeholder="00"
                @change="changeReservationHours"
              />
            </div>
            <div class="reservationTimeHourAndMinuteSeparator">:</div>
            <div class="reservationTimeMinutes">
              <Autocomplete
                id="reservationMinutes"
                :data-list="minutesList"
                :value="reservationMinutes"
                :searchable="false"
                placeholder="00"
                @change="changeReservationMinutes"
              />
            </div>
          </div>
          <ValidationError id="reservationDateTime" :error="reservationDateTimeError()" />
        </section>

        <!-- 繰り返し投稿 -->
        <div v-if="canRepeatPost" class="repeatingPost">
          <KkCheckbox
            id="isLoopPost"
            :label="$t('pages.CompaniesStorePost.setupRepeatingPost')"
            :model-value="repeatingPost"
            @update:modelValue="toggleRepeatingPost"
          />

          <div v-if="repeatingPost" class="repeatingPost__config">
            <KkInfo>
              {{ $t('pages.CompaniesStorePost.repeatingPostDescription') }}
            </KkInfo>

            <section class="repeatingPost__section">
              <h4 class="label">{{ $t('pages.CompaniesStorePost.repeatingSchedule') }}</h4>
              <div class="c-select">
                <select :value="repeatingPostScheduleKind" @change="(e) => updateRepeatingPostScheduleKind(e.target.value)">
                  <option v-for="option in scheduleOptions" :key="option.key" :value="option.key">
                    {{ option.value }}
                  </option>
                </select>
              </div>
            </section>
          </div>
        </div>
      </template>

      <!-- docomo ブランドページへの表示設定 -->
      <div v-if="post.services.includes('docomo')" class="docomo">
        <KkCheckbox
          id="docomo"
          :label="`${ $t('pages.CompaniesStorePost.displayedOnTheDocomoBrandPage') }`"
          :model-value="post.docomo.postToBrandPage"
          @update:modelValue="toggleDocomoPostToBrandPage"
        />
        <KkInfo class="docomo__info">
          {{ $t('pages.CompaniesStorePost.postDocomoBrandPageInfo') }}
        </KkInfo>
      </div>

      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            outlined
            :text="$t('labels.cancel')"
            @click="cancelAlert()"
          />
        </div>
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            :text="$t('pages.CompaniesStorePost.draftedPost')"
            @click="openActionModal(true)"
          />
        </div>
        <div class="buttonGroup__item">
          <KkSplitButton
            v-if="isAvailableImmediatePost"
            :current-option="currentPostTimingOption"
            :options="postTimingOptions"
            @select-option="handleSelectOption"
            @click="openActionModal(false)"
          >
            <template #option="slotProps">
              {{ slotProps.option.name }}
            </template>
          </KkSplitButton>
          <KkButton
            v-else
            color="primary"
            :disabled="!!reservationDateTimeError()"
            :text="needApproval ? $t('pages.CompaniesStorePost.sendApproval') : $t('pages.CompaniesStorePost.makeReservation')"
            @click="openActionModal(false)"
          />
        </div>
      </div>
    </template>
  </KkBox>
</template>

<script>
import {KkBox, KkButton, KkCheckbox, KkSplitButton} from '../../../../base'
import {KkInfo} from '../../../../ui'
import DatePicker from '../../../../DatePicker'
import moment from 'moment'
import ValidationError from '../../../../ValidationError'
import Autocomplete from '../../../../Autocomplete'
import {mapMutations, mapActions, mapState, mapGetters} from 'vuex'
import {dayOfWeek} from '../../../../../modules/dayOfWeekConst'
import {actionEvent} from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'KkPromotionCompaniesStorePostsSetting',
  components: {
    KkBox,
    KkInfo,
    DatePicker,
    ValidationError,
    Autocomplete,
    KkButton,
    KkCheckbox,
    KkSplitButton,
  },
  props: {
    needApproval: {
      type: Boolean,
      required: true
    },
    isPostEdit: {
      type: Boolean,
      required: true
    },
  },
  emits: ['open-action-modal', 'cancel-alert'],
  data() {
    return {
      reservationDate: new Date(),
      reservationHours: '00',
      reservationMinutes: '00',
      currentPostTimingOption: null,
      postTimingOptions: [
        { key: 'reserve', name: this.$t('pages.CompaniesStorePost.postTimingOption.reserve') },
        { key: 'immediate', name: this.$t('pages.CompaniesStorePost.postTimingOption.immediate') }
      ]
    }
  },
  computed: {
    ...mapState({
      post: state => state.companiesStorePosts.post,
      reservationDateTime: state => state.companiesStorePosts.reservationDateTime,
      repeatingPost: state => state.companiesStorePosts.repeatingPost,
      repeatingPostScheduleKind: state => state.companiesStorePosts.repeatingPostScheduleKind,
    }),
    ...mapGetters({
      isNewPost: 'companiesStorePosts/isNewPost',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      useServiceName: 'companiesCompany/useServiceName',
    }),
    isAvailableImmediatePost() {
      return this.showOnlyOwnerAuthority || !this.needApproval
    },
    disabledDates() {
      return {
        from: moment(),
        to: moment().add(6, 'month'),
      }
    },
    hoursList: () => {
      const hours = []
      for (let step = 0; step < 24; step++) {
        hours.push(('00' + step).slice(-2))
      }
      return hours
    },
    minutesList: () => {
      const minutes = []
      for (let step = 0; step < 6; step++) {
        minutes.push((step + '00').slice(0, 2))
      }
      return minutes
    },
    dayOfWeekOfReservationDate() {
      const dowNumber = moment(this.reservationDate).day()
      return Object.values(dayOfWeek)[dowNumber]
    },
    dayOfWeekCountOfReservationDate() {
      return Math.floor((this.reservationDate.getDate() - 1) / 7) + 1
    },
    scheduleOptions() {
      return [
        { key: 'weekly', value: `毎週 ${this.dayOfWeekOfReservationDate}`},
        { key: 'wdayIndex', value: `毎月 第${this.dayOfWeekCountOfReservationDate}${this.dayOfWeekOfReservationDate}`},
        { key: 'monthly', value: `毎月 ${moment(this.reservationDate).date()}日`},
      ]
    },
    reservationTime() {
      return `${this.reservationHours}:${this.reservationMinutes}`
    },
    calculateReservationDateTime() {
      if (!this.reservationDate) return null

      let reservationDateTime
      const time = this.reservationTime.split(':')
      reservationDateTime = new Date(
        this.reservationDate.getFullYear(),
        this.reservationDate.getMonth(),
        this.reservationDate.getDate(),
        Number(time[0]),
        Number(time[1]),
      )

      return reservationDateTime
    },
    canRepeatPost() {
      return this.useServiceName !== 'retty'
    },
  },
  created() {
    this.currentPostTimingOption = this.postTimingOptions.find(option => option.key === 'reserve')
    // 編集中の即時投稿で編集者が即時投稿を使える場合
    if (this.isAvailableImmediatePost && !this.isNewPost && !this.reservationDateTime) {
      this.currentPostTimingOption = this.postTimingOptions.find(option => option.key === 'immediate')
      this.reservationDate = null
    } else if (this.reservationDateTime) {
      this.reservationDate = this.reservationDateTime
      this.reservationHours = moment(this.reservationDateTime).format('HH')
      this.reservationMinutes = moment(this.reservationDateTime).format('mm')
    }
    this.updateReservationDateTime(this.calculateReservationDateTime)
  },
  methods: {
    ...mapActions({
      updateSubmitActionModalType: 'companiesStorePosts/updateSubmitActionModalType',
      setImmediatePost: 'companiesStorePosts/setImmediatePost'
    }),
    ...mapMutations({
      updateReservationDateTime: 'companiesStorePosts/updateReservationDateTime',
      updateRepeatingPost: 'companiesStorePosts/updateRepeatingPost',
      updateDocomoPostToBrandPage: 'companiesStorePosts/updateDocomoPostToBrandPage',
      updateRepeatingPostScheduleKind: 'companiesStorePosts/updateRepeatingPostScheduleKind'
    }),
    handleSelectOption(selectedOption) {
      this.currentPostTimingOption = selectedOption
      if (this.currentPostTimingOption.key === 'immediate') {
        this.setImmediatePost()
        if (!this.isPostEdit) {
          actionEvent('NowPost')
        }
      } else {
        if (!this.reservationDate) this.reservationDate = new Date()
        this.updateReservationDateTime(this.calculateReservationDateTime)
        if (!this.isPostEdit) {
          actionEvent('SchedulePost')
        }
      }
    },
    reservationDateTimeError() {
      const now = new Date
      if (!this.calculateReservationDateTime) {
        return this.$t('pages.CompaniesStorePost.reservationDateTimeRequired')
      } else if (this.calculateReservationDateTime.getTime() < now.getTime()) {
        return this.$t('pages.CompaniesStorePost.FutureReservationDateTime')
      }
    },
    changeReservationDate(reservationDate) {
      this.reservationDate = reservationDate
      this.updateReservationDateTime(this.calculateReservationDateTime)
    },
    changeReservationHours(reservationHours) {
      this.reservationHours = reservationHours
      this.updateReservationDateTime(this.calculateReservationDateTime)
    },
    changeReservationMinutes(reservationMinutes) {
      this.reservationMinutes = reservationMinutes
      this.updateReservationDateTime(this.calculateReservationDateTime)
    },
    openActionModal(isDraftedPost) {
      let action = 'draft'

      if (!isDraftedPost) {
        if (this.reservationDateTimeError() && this.currentPostTimingOption.key === 'reserve') {
          alert(this.reservationDateTimeError())
          return
        }

        action = this.needApproval ? 'request' : 'post'
      }

      this.updateSubmitActionModalType(action)
      this.$emit('open-action-modal')
    },
    cancelAlert() {
      this.$emit('cancel-alert')
      if (!this.isPostEdit) {
        actionEvent('CancelNewPost')
      }
    },
    toggleRepeatingPost(value) {
      this.updateRepeatingPost(value)
      if (!this.isPostEdit) {
        actionEvent('RepeatPost')
      }
    },
    toggleDocomoPostToBrandPage(value) {
      this.updateDocomoPostToBrandPage(value)
    },
  }
}
</script>

<style scoped lang="scss">
.name {
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  line-height: 1.33;
  + .section {
    margin-top: 0;
  }
}
.label {
  margin-bottom: 8px;
  font-size: 12px;
}
.section {
  margin-top: 32px;
}
.docomo {
  margin-top: 24px;

  &__info {
    margin-top: 16px;
  }
}
.reservationTime {
  display: flex;
  align-items: center;
  .reservationTimeHours {
    width: 45%;
  }
  .reservationTimeHourAndMinuteSeparator {
    width: 10%;
    text-align: center;
  }
  .reservationTimeMinutes {
    width: 45%;
  }
}
.repeatingPost {
  margin-top: 32px;
  &__config {
    margin-top: 16px;
  }
  &__section {
    margin-top: 24px;
  }
}
.buttonGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}
:deep(.info__text) {
  white-space: unset;
}
</style>
